import React from "react";

import {
  ActionButton,
  FontIcon,
  FontSizes,
  Heading,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { NzPracticeAccContractDto } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { PracticeAccContractsStyles } from "@modules/settings/screens/practice/components/practice-acc-contracts/PracticeAccContracts.styles.tsx";
import {
  AccPracticeContractLabels,
  AccPracticeContractType
} from "@modules/settings/screens/practice/components/practice-details/PracticeDetailsForm.types.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

interface SpecifiedTreatmentFieldsWrapperProps {
  children: (
    associatedBusinessRoles: { key: string; text: string }[]
  ) => React.ReactNode;
  addItem: (item: NzPracticeAccContractDto) => void;
}

const TOOLTIP_CONTENT =
  "Also referred to as a regulation contract by ACC. Each provider type has a generic ID determined by ACC and the ID is included in invoices transmitted to ACC";

const emptySpecifiedTreatmentFields: NzPracticeAccContractDto = {
  contractId: "",
  businessRoleCode: "",
  isSubscribed: false,
  accPracticeContractTypeCode:
    AccPracticeContractType.SpecifiedTreatmentProvider
};

export const SpecifiedTreatmentFieldsWrapper: React.FC<
  SpecifiedTreatmentFieldsWrapperProps
> = ({ children, addItem }) => {
  const { practice } = useStores();
  const getAssociatedBusinessRoleCodes = async () => {
    const businessRoles = practice.ref.accProviderContractTypes.values.filter(
      type =>
        type.accPracticeContractTypeCode ===
        AccPracticeContractType.SpecifiedTreatmentProvider
    );

    return businessRoles.map(({ businessRoleCode, providerTypeText }) => ({
      key: businessRoleCode,
      text: providerTypeText
    }));
  };

  return (
    <DataFetcher fetch={getAssociatedBusinessRoleCodes}>
      {businessRoleCodes => {
        return (
          <Stack styles={PracticeAccContractsStyles.contractTypeItem}>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Stack.Item styles={{ root: { flexGrow: 1, flexBasis: 0 } }}>
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                  <Heading labelPaddings>
                    {AccPracticeContractLabels.STP}
                  </Heading>
                  <TooltipHost
                    content={TOOLTIP_CONTENT}
                    styles={{
                      root: {
                        display: "flex",
                        alignItems: "center"
                      }
                    }}
                  >
                    <FontIcon
                      iconName="Info"
                      styles={{
                        root: { fontSize: FontSizes.size12 }
                      }}
                    />
                  </TooltipHost>
                </Stack>
              </Stack.Item>
              <Stack.Item styles={{ root: { flexGrow: 1, flexBasis: 0 } }}>
                <Heading labelPaddings hasAsterisk>
                  {AccPracticeContractLabels.BR}
                </Heading>
              </Stack.Item>
            </Stack>
            <Stack tokens={{ childrenGap: 8 }}>
              {children(businessRoleCodes)}
            </Stack>

            <ActionButton
              iconProps={{ iconName: "Add" }}
              text="Add another"
              onClick={() => addItem(emptySpecifiedTreatmentFields)}
              styles={{
                root: {
                  alignSelf: "baseline",
                  marginTop: "8px",
                  paddingLeft: 0
                }
              }}
            />
          </Stack>
        );
      }}
    </DataFetcher>
  );
};
